import { useCallback, useLayoutEffect, useRef } from "react";

interface UseCloseAfterSaveOptions {
    /** Id of the entity the drawer shows right now */
    shownId: string | undefined;
    /** Unsaved changes in the shown editor (also the ones made while the save was pending) */
    isDirty: boolean;
    close: () => void;
}

/**
 * Returns the callback for "this save finished". It closes the drawer only when the saved entity is still the shown
 * one and nothing new is waiting to be saved: a save of another entity, or edits made while the request was pending,
 * must not be thrown away by a direct close (which does not go through the drawer's `onBeforeClose`).
 */
export const useCloseAfterSave = ({ shownId, isDirty, close }: UseCloseAfterSaveOptions) => {
    const latest = useRef({ shownId, isDirty, close });
    // A layout effect, so it is up to date as soon as the commit is visible: a passive one could still be pending
    // when a fast save finishes right after the drawer switched entities.
    useLayoutEffect(() => {
        latest.current = { shownId, isDirty, close };
    }, [shownId, isDirty, close]);

    return useCallback((savedId: string) => {
        const { shownId: current, isDirty: dirty, close: closeDrawer } = latest.current;
        if (savedId === current && !dirty) {
            closeDrawer();
        }
    }, []);
};
